<script setup lang="ts">
// 폰 앱 레이아웃: 하단 탭(답장·일기·지도) + 성향 카드 시트 + 관리 슬라이드업.
// 데스크톱 3단(WebShell)의 로직을 그대로 쓰되 화면만 폰에 맞춘다. 컴포넌트는 web/ 것을 재사용.
import { computed, onUnmounted, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { BRAND } from '../../brand'
import { icon } from '../../lib/icons'
import { useUiStore } from '../../stores/ui'
import { useVaultStore } from '../../stores/vault'
import { isBrandBuild } from '../../brand'
import PersonCard from '../web/PersonCard.vue'
import Manage from '../web/Manage.vue'
import RelMap from '../web/RelMap.vue'
import Compose from '../web/Compose.vue'
import Diary from '../web/Diary.vue'
import NewPeoplePrompt from '../NewPeoplePrompt.vue'

const { t } = useI18n()
const ui = useUiStore()
const vault = useVaultStore()
const emit = defineEmits<{ toast: [msg: string]; openSettings: []; showCover: []; openAccount: [] }>()
const brand = isBrandBuild

type Tab = 'compose' | 'diary' | 'map'
const tab = ref<Tab>('compose')
const mapMode = ref<'rel' | 'org'>('rel')
const current = ref(vault.people[0]?.id ?? '')
const writeText = ref('')
const prefill = ref('')
const sheetOpen = ref(false)     // 성향 카드
const manageOpen = ref(false)    // 사람·조직·관계
const manageFocus = ref<string | undefined>()  // 관리 열 때 특정 사람으로(새 사람 포착)
const mapSelected = ref<string | null>(null)
const relMapRef = ref<{ grow: () => void; ping: (id: string) => void } | null>(null)
// 일기/기록에서 포착된 새 사람 후보(아직 등록 안 됨). 무시한 이름은 세션 동안 다시 안 묻는다.
const newPeople = ref<string[]>([])
const dismissed = new Set<string>()
function onNewPeople(names: string[]) { newPeople.value = names.filter(n => !dismissed.has(n)) }
async function addNewPerson(name: string) {
  const p = await vault.createPerson(name)
  relMapRef.value?.grow()
  newPeople.value = newPeople.value.filter(n => n !== name)
  manageFocus.value = p.id // 자동 이동 안 함(일기 작성 흐름 유지). 나중에 관리를 열면 이 사람으로 포커스
  emit('toast', t('newp.added', { name }))
}
function ignoreNewPeople() { newPeople.value.forEach(n => dismissed.add(n)); newPeople.value = [] }

// 모달(성향 시트·관리)이 열리면 꾹-녹음 FAB를 숨긴다(.mshell이 fixed라 FAB가 시트 위로 새는 걸 막음)
watchEffect(() => { ui.overlayOpen = sheetOpen.value || manageOpen.value })
onUnmounted(() => { ui.overlayOpen = false })

// 폰 앱은 화려하게 — 업무 용어(stealth) 대신 평소 라벨을 쓴다(decisions.md: 앱은 화려한 연출)
const brandLabel = computed(() => BRAND.appName)
const people = computed(() => [...vault.people].sort((a, b) => b.close - a.close))

function pickPerson(id: string) {
  if (id === current.value) { sheetOpen.value = true; return } // 선택된 사람 다시 탭 → 성향 카드
  current.value = id
}
function onComposeGood(id: string) {
  relMapRef.value?.grow(); relMapRef.value?.ping(id)
  const p = vault.people.find(x => x.id === id)
  emit('toast', t('compose.good') + (p ? ` · ${p.name}` : ''))
}
function onMapSelect(id: string | null) {
  mapSelected.value = id
  if (id) current.value = id
}
function onMapMe() { emit('toast', t('left.meLevel', { lv: Math.floor(vault.entries.length / 10) + 1 })) }
function replyToSelected() { if (mapSelected.value) { current.value = mapSelected.value; tab.value = 'compose' } }
function editSelected() { manageOpen.value = true }
function onUseThread(txt: string) {
  const p = vault.people.find(x => x.name && txt.includes(x.name))
  if (p) current.value = p.id
  prefill.value = txt
  tab.value = 'compose'
}
function onDiaryGrow(foundIds: string[]) {
  relMapRef.value?.grow()
  for (const id of foundIds) relMapRef.value?.ping(id)
}
async function record() {
  const txt = writeText.value.trim()
  if (!txt) return
  writeText.value = ''
  const { insight, foundIds, newNames } = await vault.logEntry(txt)
  relMapRef.value?.grow()
  for (const id of foundIds) relMapRef.value?.ping(id)
  emit('toast', insight)
  onNewPeople(newNames)
}
</script>

<template>
  <div class="mshell" :class="{ rm: !ui.motionActive }">
    <!-- ── 답장 ── -->
    <section v-show="tab === 'compose'" class="mpane">
      <header class="mtop">
        <div class="brand">{{ brandLabel }}</div>
        <div class="mtop-r">
          <button v-if="brand" class="icon-btn" :aria-label="t('account.title')" @click="emit('openAccount')" v-html="icon('user')"></button>
          <button class="icon-btn" :aria-label="t('settings.cover')" @click="emit('showCover')" v-html="icon('eyeOff')"></button>
          <button class="icon-btn" :aria-label="t('header.settings')" @click="emit('openSettings')" v-html="icon('gear')"></button>
          <button class="icon-btn" :aria-label="t('settings.lockNow')" @click="vault.lock()" v-html="icon('lock')"></button>
        </div>
      </header>
      <div class="mlabel">
        <span>{{ t('nav.pickLabel') }}</span>
        <button class="mcard-link" @click="sheetOpen = true">{{ t('nav.cardLink') }} ›</button>
      </div>
      <div class="mstrip">
        <button
          v-for="(p, i) in people" :key="p.id"
          class="mperson" :class="{ sel: p.id === current }" @click="pickPerson(p.id)"
        >
          <span class="mblob-wrap"><span class="mblob" :style="{ '--c1': p.c1, '--c2': p.c2, '--dl': `${-i * 1.1}s` }"></span></span>
          <span class="mname">{{ p.name }}</span>
        </button>
      </div>
      <Compose :person-id="current" :prefill="prefill" @toast="emit('toast', $event)" @good="onComposeGood" />
    </section>

    <!-- ── 일기 ── -->
    <section v-show="tab === 'diary'" class="mpane">
      <header class="mtop">
        <div class="brand-row"><div class="brand">{{ brandLabel }}</div><small class="brand-sub">{{ t('nav.diary') }}</small></div>
        <button class="icon-btn" :aria-label="t('settings.lockNow')" @click="vault.lock()" v-html="icon('lock')"></button>
      </header>
      <Diary @toast="emit('toast', $event)" @grow="onDiaryGrow" @use-thread="onUseThread" @new-people="onNewPeople" />
    </section>

    <!-- ── 회사 지도 ── -->
    <section v-if="tab === 'map'" class="mpane">
      <header class="mtop">
        <div class="brand-row"><div class="brand">{{ brandLabel }}</div><small class="brand-sub">{{ t('nav.map') }}</small></div>
        <div class="mtop-r">
          <button class="icon-btn" :aria-label="t('nav.manage')" @click="manageOpen = true" v-html="icon('pencil')"></button>
          <button class="icon-btn" :aria-label="t('settings.lockNow')" @click="vault.lock()" v-html="icon('lock')"></button>
        </div>
      </header>
      <div class="map-head">
        <div class="seg-toggle">
          <button :class="{ sel: mapMode === 'rel' }" @click="mapMode = 'rel'">{{ t('map.rel') }}</button>
          <button :class="{ sel: mapMode === 'org' }" @click="mapMode = 'org'">{{ t('map.org') }}</button>
        </div>
        <div class="legend">
          <span><i style="border-color:#3DBE8B"></i><b>{{ t('map.friend') }}</b></span>
          <span><i style="border-color:#E5484D;border-top-style:dashed"></i><b>{{ t('map.tension') }}</b></span>
        </div>
      </div>
      <div class="map-wrap"><RelMap ref="relMapRef" :mode="mapMode" :calm="!ui.motionActive" @select="onMapSelect" @me="onMapMe" /></div>
      <div v-if="mapSelected" class="minfo">
        <PersonCard :person-id="mapSelected" />
        <div class="minfo-act">
          <button class="minfo-go" @click="replyToSelected">{{ t('diary.useReply') }}</button>
          <button class="minfo-edit" @click="editSelected" v-html="icon('pencil')"></button>
        </div>
      </div>
      <div class="write">
        <input v-model="writeText" :placeholder="t('map.writePlaceholder')" @keydown.enter="record" />
        <button @click="record">{{ t('map.writeButton') }}</button>
      </div>
      <div class="write-hint">{{ t('map.writeHint') }}</div>
    </section>

    <!-- ── 성향 카드 시트 ── -->
    <div v-if="sheetOpen" class="msheet-scrim" @click="sheetOpen = false"></div>
    <div v-if="sheetOpen" class="msheet" role="dialog" aria-modal="true">
      <div class="grab"></div>
      <PersonCard :person-id="current" />
    </div>

    <!-- ── 사람·조직·관계 관리(아래에서 올라옴) ── -->
    <section v-if="manageOpen" class="mmanage">
      <header class="mtop"><b class="mmanage-title">{{ t('nav.manage') }}</b><button class="mdone" @click="manageOpen = false; manageFocus = undefined">{{ t('nav.done') }}</button></header>
      <Manage :focus-id="manageFocus" @toast="emit('toast', $event)" />
    </section>

    <!-- ── 새 사람 포착 프롬프트(일기/기록 후) ── -->
    <div v-if="newPeople.length" class="mnewp">
      <NewPeoplePrompt :names="newPeople" @add="addNewPerson" @ignore="ignoreNewPeople" />
    </div>

    <!-- ── 하단 탭바 ── -->
    <nav class="mtabbar">
      <button :class="{ sel: tab === 'compose' }" @click="tab = 'compose'"><span v-html="icon('chat')"></span>{{ t('nav.reply') }}</button>
      <button :class="{ sel: tab === 'diary' }" @click="tab = 'diary'"><span v-html="icon('book')"></span>{{ t('nav.diary') }}</button>
      <button :class="{ sel: tab === 'map' }" @click="tab = 'map'"><span v-html="icon('map')"></span>{{ t('nav.map') }}</button>
    </nav>
  </div>
</template>
